import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { caseStudies, caseStudySectors, CASE_STUDY_CATEGORIES } from "@/data/caseStudies";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

export const CaseStudies = () => {
  useSeo({
    title: "Case Studies · India Sourcing · Asia Sourcing India",
    description:
      "20 in-depth sourcing case studies — decorative lighting, furniture, home décor, ceramics, glass, marble, garden, textile programmes from India. Real outcomes, measurable results.",
    path: "/case-studies",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          "@id": `${SITE_URL}/case-studies#list`,
          name: "Asia Sourcing India — Case Studies",
          numberOfItems: caseStudies.length,
          itemListElement: caseStudies.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.title,
            url: `${SITE_URL}/case-studies/${c.slug}`,
          })),
        },
        breadcrumb([
          { name: "Home", item: `${SITE_URL}/` },
          { name: "Case Studies", item: `${SITE_URL}/case-studies` },
        ]),
      ],
    },
    jsonLdId: "case-studies-hub-jsonld",
  });

  const [filter, setFilter] = useState("All");
  const visible = useMemo(() => {
    if (filter === "All") return caseStudies;
    return caseStudies.filter((c) => c.sector === filter);
  }, [filter]);

  const featured = caseStudies.find((c) => c.featured);

  return (
    <div data-testid="case-studies-page">
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 3rem" }}>
        <Reveal>
          <p className="eyebrow" data-testid="cs-hub-eyebrow">Case Studies · Authority Content</p>
        </Reveal>
        <WordReveal as="h1" text="20 in-depth sourcing case studies from India." className="display" stagger={60} testId="cs-hub-h1" />
        <Reveal as="p" delay={200} style={{ maxWidth: "72ch", color: "var(--ink-soft)", fontSize: "1.12rem", marginTop: "1.4rem", lineHeight: 1.7 }}>
          Every case study documents the same discipline — cluster-first supplier mapping, formal CAD-to-container workflow, dedicated QC on the factory floor, and Delhi NCR-hub consolidation. Together they answer the question international buyers most often ask: <em>&ldquo;What does a decade-long India sourcing programme actually look like?&rdquo;</em>
        </Reveal>
      </header>

      {featured && (
        <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }} data-testid="cs-hub-featured">
          <Reveal as="p" className="eyebrow">★ Featured — 10-year decorative lighting partnership</Reveal>
          <Reveal>
            <Link to={`/case-studies/${featured.slug}`} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: "2rem", alignItems: "center", textDecoration: "none", color: "inherit", marginTop: "1rem" }}>
              {featured.image && (
                <img src={featured.image} alt={featured.imageAlt} width="1200" height="675" loading="eager" style={{ width: "100%", height: "auto", maxHeight: "440px", objectFit: "cover", border: "1px solid var(--line)" }} />
              )}
              <div>
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                  {featured.sector} · {featured.region} · {featured.duration}
                </p>
                <h2 className="display" style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", margin: ".8rem 0", letterSpacing: "-.01em", lineHeight: 1.15 }}>
                  {featured.title}
                </h2>
                <p style={{ color: "var(--ink-soft)", fontSize: "1.05rem", lineHeight: 1.65, margin: 0 }}>
                  {featured.executiveSummary[0]}
                </p>
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".7rem", letterSpacing: ".14em", textTransform: "uppercase", marginTop: "1.4rem" }}>
                  Read the full case study →
                </p>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }} data-testid="cs-hub-categories">
        <Reveal as="p" className="eyebrow">§ Browse by category — 10 topical clusters</Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem", marginTop: "1.5rem" }}>
          {CASE_STUDY_CATEGORIES.map((c) => (
            <Link
              key={c.id}
              to={`/case-studies/${c.id}`}
              style={{ padding: "1.2rem 1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit", display: "block" }}
              data-testid={`cs-hub-category-${c.id}`}
            >
              <p style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: 0, letterSpacing: "-.005em" }}>{c.name}</p>
              <p style={{ color: "var(--ink-soft)", fontSize: ".9rem", margin: ".6rem 0 0", lineHeight: 1.5 }}>{c.tagline}</p>
              <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", marginTop: ".8rem" }}>
                {(c.id === "home-decor" ? caseStudies.filter(cs => (cs.categories || []).includes("home-decor")).length : (caseStudies.filter(cs => (cs.categories || []).includes(c.id))).length)} case {caseStudies.filter(cs => (cs.categories || []).includes(c.id)).length === 1 ? "study" : "studies"} →
              </p>
            </Link>
          ))}
        </div>
      </section>

      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }} data-testid="cs-hub-grid-section">
        <div className="filter-bar" data-testid="cs-hub-filters">
          {caseStudySectors.map((s) => (
            <button
              key={s}
              className={`chip ${filter === s ? "active" : ""}`}
              onClick={() => setFilter(s)}
              data-testid={`cs-hub-filter-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {s}
            </button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.4rem", marginTop: "2rem" }}>
          {visible.map((c) => (
            <Reveal key={c.slug}>
              <Link
                to={`/case-studies/${c.slug}`}
                style={{ display: "block", padding: "1.6rem 1.4rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit", height: "100%" }}
                data-testid={`cs-hub-tile-${c.slug}`}
              >
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                  {c.sector} · {c.region}
                </p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".55rem 0 .6rem", letterSpacing: "-.005em", lineHeight: 1.3 }}>
                  {c.title}
                </p>
                <p style={{ color: "var(--ink-soft)", fontSize: ".92rem", margin: 0, lineHeight: 1.55 }}>
                  {c.duration} · {c.readTime}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}>
        <Reveal as="p" className="eyebrow">Ready to build your own?</Reveal>
        <Reveal as="h2" className="display">Send a sourcing brief.</Reveal>
        <Reveal as="div" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "2rem" }}>
          <Link to="/contact" className="btn btn-fill btn-lg btn-glow" data-testid="cs-hub-cta">
            Start an enquiry
          </Link>
          <Link to="/services" className="btn btn-lg">See all services</Link>
        </Reveal>
      </section>
    </div>
  );
};

export default CaseStudies;
